import { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Brush,
} from "recharts";
import { cn } from "@/lib/utils";
import { Loader2, TrendingUp, TrendingDown, Activity, BarChart3, CandlestickChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface AdvancedChartProps {
  coinId: string;
  coinTicker?: string;
}

type ChartType = "line" | "candle" | "area";
type Indicator = "ma7" | "ma25" | "ma99" | "volume" | "rsi" | "macd" | "bb";

const timeframes = [
  { id: "1M", label: "1m", minutes: 1 },
  { id: "5M", label: "5m", minutes: 5 },
  { id: "15M", label: "15m", minutes: 15 },
  { id: "1H", label: "1H", minutes: 60 },
  { id: "4H", label: "4H", minutes: 240 },
  { id: "1D", label: "1D", minutes: 1440 },
  { id: "1W", label: "1W", minutes: 10080 },
];

interface CandleData {
  time: string;
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  ma7?: number;
  ma25?: number;
  ma99?: number;
  upperBB?: number;
  lowerBB?: number;
  rsi?: number;
}

function calculateMA(data: CandleData[], period: number): number[] {
  return data.map((_, index) => {
    if (index < period - 1) return 0;
    const slice = data.slice(index - period + 1, index + 1);
    return slice.reduce((sum, d) => sum + d.close, 0) / period;
  });
}

function calculateRSI(data: CandleData[], period: number = 14): number[] {
  const changes = data.map((d, i) => (i === 0 ? 0 : d.close - data[i - 1].close));
  const gains = changes.map((c) => (c > 0 ? c : 0));
  const losses = changes.map((c) => (c < 0 ? -c : 0));

  return data.map((_, index) => {
    if (index < period) return 50;
    const avgGain = gains.slice(index - period, index).reduce((a, b) => a + b, 0) / period;
    const avgLoss = losses.slice(index - period, index).reduce((a, b) => a + b, 0) / period;
    if (avgLoss === 0) return 100;
    const rs = avgGain / avgLoss;
    return 100 - 100 / (1 + rs);
  });
}

function calculateBollingerBands(data: CandleData[], period: number = 20, stdDev: number = 2) {
  const ma = calculateMA(data, period);
  return data.map((_, index) => {
    if (index < period - 1) return { upper: 0, lower: 0, middle: 0 };
    const slice = data.slice(index - period + 1, index + 1);
    const avg = ma[index];
    const variance = slice.reduce((sum, d) => sum + Math.pow(d.close - avg, 2), 0) / period;
    const std = Math.sqrt(variance);
    return {
      upper: avg + stdDev * std,
      lower: avg - stdDev * std,
      middle: avg,
    };
  });
}

export function AdvancedChart({ coinId, coinTicker = "TOKEN" }: AdvancedChartProps) {
  const [timeframe, setTimeframe] = useState("15M");
  const [chartType, setChartType] = useState<ChartType>("line");
  const [indicators, setIndicators] = useState<Indicator[]>(["volume", "ma7", "ma25"]);
  const [showOrderBook, setShowOrderBook] = useState(false);

  const toggleIndicator = useCallback((ind: Indicator) => {
    setIndicators((prev) =>
      prev.includes(ind) ? prev.filter((i) => i !== ind) : [...prev, ind]
    );
  }, []);

  const { data: snapshots, isLoading } = useQuery({
    queryKey: ["advanced-chart", coinId, timeframe],
    queryFn: async () => {
      const selectedTimeframe = timeframes.find((t) => t.id === timeframe);
      const minutesAgo = (selectedTimeframe?.minutes || 15) * 500;
      const cutoffDate = new Date(Date.now() - minutesAgo * 60 * 1000).toISOString();

      const { data, error } = await supabase
        .from("market_cap_snapshots")
        .select("*")
        .eq("coin_id", coinId)
        .gte("timestamp", cutoffDate)
        .order("timestamp", { ascending: true })
        .limit(1000);

      if (error) throw error;
      return data ?? [];
    },
    refetchInterval: 5000,
  });

  const chartData: CandleData[] = useMemo(() => {
    // Fallback: If no snapshots, try to fetch trades to build candles
    if (!snapshots || snapshots.length < 2) {
      // We need to fetch trades if we don't have snapshots
      // Ideally this would be a separate query, but for now we'll rely on the parent or a secondary fetch if needed.
      // For this implementation, we will show "No Data" rather than fake data if snapshots are missing.
      return [];
    }

    const data = snapshots.map((s) => ({
      time: new Date(s.timestamp ?? Date.now()).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
      timestamp: new Date(s.timestamp ?? Date.now()).getTime(),
      open: Number(s.price),
      high: Number(s.price), // In a snapshot based system, high/low might be lost without aggregation
      low: Number(s.price),
      close: Number(s.price),
      volume: Number(s.volume_24h ?? 0),
    }));

    // Add indicators
    const ma7 = calculateMA(data, 7);
    const ma25 = calculateMA(data, 25);
    const ma99 = calculateMA(data, 99);
    const rsi = calculateRSI(data);
    const bb = calculateBollingerBands(data);

    return data.map((d, i) => ({
      ...d,
      ma7: ma7[i] || d.close,
      ma25: ma25[i] || d.close,
      ma99: ma99[i] || d.close,
      rsi: rsi[i],
      upperBB: bb[i]?.upper || d.close,
      lowerBB: bb[i]?.lower || d.close,
    }));
  }, [snapshots, coinId]);

  const stats = useMemo(() => {
    if (chartData.length < 2) return { change: 0, high: 0, low: 0, volume: 0 };
    const first = chartData[0].close;
    const last = chartData[chartData.length - 1].close;
    const change = ((last - first) / first) * 100;
    const high = Math.max(...chartData.map((d) => d.high));
    const low = Math.min(...chartData.map((d) => d.low));
    const volume = chartData.reduce((sum, d) => sum + d.volume, 0);
    return { change, high, low, volume, current: last };
  }, [chartData]);

  const isPositive = stats.change >= 0;

  return (
    <div className="rounded-xl bg-card border border-border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-mono font-bold text-foreground">
                  ${stats.current?.toFixed(8) ?? "0.00000000"}
                </span>
                <Badge variant={isPositive ? "default" : "destructive"} className="font-mono">
                  {isPositive ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                  {isPositive ? "+" : ""}
                  {stats.change.toFixed(2)}%
                </Badge>
              </div>
              <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                <span>H: ${stats.high.toFixed(8)}</span>
                <span>L: ${stats.low.toFixed(8)}</span>
                <span>Vol: ${(stats.volume / 1000).toFixed(1)}K</span>
              </div>
            </div>
          </div>

          {/* Chart Type */}
          <div className="flex gap-1 p-1 rounded-lg bg-secondary">
            <Button
              size="sm"
              variant={chartType === "line" ? "default" : "ghost"}
              onClick={() => setChartType("line")}
              className="h-7 px-2"
            >
              <Activity className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant={chartType === "candle" ? "default" : "ghost"}
              onClick={() => setChartType("candle")}
              className="h-7 px-2"
            >
              <CandlestickChart className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant={chartType === "area" ? "default" : "ghost"}
              onClick={() => setChartType("area")}
              className="h-7 px-2"
            >
              <BarChart3 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Timeframes */}
        <div className="flex flex-wrap gap-1 mt-3">
          {timeframes.map((tf) => (
            <Button
              key={tf.id}
              size="sm"
              variant={timeframe === tf.id ? "default" : "ghost"}
              onClick={() => setTimeframe(tf.id)}
              className="h-7 px-3 text-xs"
            >
              {tf.label}
            </Button>
          ))}
        </div>

        {/* Indicators */}
        <div className="flex flex-wrap gap-2 mt-3">
          {(["ma7", "ma25", "ma99", "volume", "bb"] as Indicator[]).map((ind) => (
            <Badge
              key={ind}
              variant={indicators.includes(ind) ? "default" : "outline"}
              className="cursor-pointer text-xs"
              onClick={() => toggleIndicator(ind)}
            >
              {ind.toUpperCase()}
            </Badge>
          ))}
        </div>
      </div>

      {/* Main Chart */}
      <div className="h-[400px] p-4" aria-label={`Price chart for ${coinTicker}`}>
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <BarChart3 className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">No Trading Data Yet</h3>
            <p className="text-muted-foreground max-w-sm">
              Be the first to trade!
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              {/* ... chart components ... */}
              <defs>
                <linearGradient id="priceGradientAdv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>

              <XAxis
                dataKey="time"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                interval="preserveStartEnd"
              />
              <YAxis
                yAxisId="price"
                domain={["auto", "auto"]}
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                tickFormatter={(value) => `$${value.toFixed(6)}`}
                width={70}
                orientation="right"
              />
              {indicators.includes("volume") && (
                <YAxis
                  yAxisId="volume"
                  domain={[0, "auto"]}
                  axisLine={false}
                  tickLine={false}
                  tick={false}
                  width={0}
                />
              )}

              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const d = payload[0].payload as CandleData;
                    return (
                      <div className="p-3 rounded-lg bg-card border border-border shadow-xl text-xs">
                        <p className="text-muted-foreground mb-2">{d.time}</p>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                          <span className="text-muted-foreground">Open:</span>
                          <span className="font-mono text-foreground">${d.open.toFixed(8)}</span>
                          <span className="text-muted-foreground">High:</span>
                          <span className="font-mono text-success">${d.high.toFixed(8)}</span>
                          <span className="text-muted-foreground">Low:</span>
                          <span className="font-mono text-destructive">${d.low.toFixed(8)}</span>
                          <span className="text-muted-foreground">Close:</span>
                          <span className="font-mono text-primary">${d.close.toFixed(8)}</span>
                          <span className="text-muted-foreground">Volume:</span>
                          <span className="font-mono">${(d.volume / 1000).toFixed(1)}K</span>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />

              {/* Volume bars */}
              {indicators.includes("volume") && (
                <Bar
                  yAxisId="volume"
                  dataKey="volume"
                  fill="hsl(var(--muted-foreground))"
                  opacity={0.2}
                />
              )}

              {/* Bollinger Bands */}
              {indicators.includes("bb") && (
                <>
                  <Line
                    yAxisId="price"
                    type="monotone"
                    dataKey="upperBB"
                    stroke="hsl(var(--muted-foreground))"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    dot={false}
                  />
                  <Line
                    yAxisId="price"
                    type="monotone"
                    dataKey="lowerBB"
                    stroke="hsl(var(--muted-foreground))"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    dot={false}
                  />
                </>
              )}

              {/* Moving Averages */}
              {indicators.includes("ma7") && (
                <Line
                  yAxisId="price"
                  type="monotone"
                  dataKey="ma7"
                  stroke="#f59e0b"
                  strokeWidth={1.5}
                  dot={false}
                />
              )}
              {indicators.includes("ma25") && (
                <Line
                  yAxisId="price"
                  type="monotone"
                  dataKey="ma25"
                  stroke="#8b5cf6"
                  strokeWidth={1.5}
                  dot={false}
                />
              )}
              {indicators.includes("ma99") && (
                <Line
                  yAxisId="price"
                  type="monotone"
                  dataKey="ma99"
                  stroke="#06b6d4"
                  strokeWidth={1.5}
                  dot={false}
                />
              )}

              {/* Price Line */}
              <Line
                yAxisId="price"
                type="monotone"
                dataKey="close"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={false}
                fill="url(#priceGradientAdv)"
              />

              <Brush
                dataKey="time"
                height={30}
                stroke="hsl(var(--border))"
                fill="hsl(var(--secondary))"
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
